//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import { EngagementPostType } from '@apis/instagram-user/instagramUserApiModel';
import IContentMedia from '@models/IContentMedia';
import { IInstagramPost } from '@models/IInstagramMediaItem';
import { ILabel } from '@models/ILabel';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { UniversalSearchSortDirection } from '@models/UniversalSearchSortDirection';
import { UniversalSearchSortKey } from '@models/UniversalSearchSortKey';

export enum INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS {
  SET_DATE_RANGE = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/SET_DATE_RANGE',
  SET_POST_TYPE = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/SET_POST_TYPE',
  SET_PAGE_PARAMETERS = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/SET_PAGE_PARAMETERS',
  SET_SELECTED_TREND_GRAPH_VALUE = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/SET_SELECTED_TREND_GRAPH_VALUE',
  SET_POST_VIEW_TYPE = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/SET_POST_VIEW_TYPE',

  SET_SELECTED_LABELS = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/SET_SELECTED_LABELS',

  REQUEST_LABELS = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_LABELS',
  REQUEST_LABELS_SUCCEEDED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_LABELS_SUCCEEDED',
  REQUEST_LABELS_FAILED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_LABELS_FAILED',

  REQUEST_POSTS = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_POSTS',
  RECEIVE_POSTS_SUCCEEDED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_POSTS_SUCCEEDED',
  RECEIVE_POSTS_FAILED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_POSTS_FAILED',

  REQUEST_POSTS_STATS = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_POSTS_STATS',
  RECEIVE_POSTS_STATS_SUCCEEDED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_POSTS_STATS_SUCCEEDED',
  RECEIVE_POSTS_STATS_FAILED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_POSTS_STATS_FAILED',

  REQUEST_RECENT_POSTS = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_RECENT_POSTS',
  RECEIVE_RECENT_POSTS_SUCCEEDED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_RECENT_POSTS_SUCCEEDED',
  RECEIVE_RECENT_POSTS_FAILED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_RECENT_POSTS_FAILED',

  REQUEST_LIVE_POSTS = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_LIVE_POSTS',
  RECEIVE_LIVE_POSTS_SUCCEEDED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_LIVE_POSTS_SUCCEEDED',
  RECEIVE_LIVE_POSTS_FAILED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_LIVE_POSTS_FAILED',

  REQUEST_MENTIONED_POSTS = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_MENTIONED_POSTS',
  RECEIVE_MENTIONED_POSTS_SUCCEEDED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_MENTIONED_POSTS_SUCCEEDED',
  RECEIVE_MENTIONED_POSTS_FAILED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_MENTIONED_POSTS_FAILED',

  REQUEST_COMMENTS_EXPORT = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_COMMENTS_EXPORT',
  RECEIVE_COMMENTS_EXPORT_SUCCEEDED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_COMMENTS_EXPORT_SUCCEEDED',
  RECEIVE_COMMENTS_EXPORT_FAILED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_COMMENTS_EXPORT_FAILED',

  REQUEST_EXPORT_POST_PERFORMANCE_TO_EMAIL = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_EXPORT_POST_PERFORMANCE_TO_EMAIL',
  RECEIVE_EXPORT_POST_PERFORMANCE_TO_EMAIL_SUCCEEDED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_EXPORT_POST_PERFORMANCE_TO_EMAIL_SUCCEEDED',
  RECEIVE_EXPORT_POST_PERFORMANCE_TO_EMAIL_FAILED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_EXPORT_POST_PERFORMANCE_TO_EMAIL_FAILED',

  REQUEST_POST_PERFORMANCE_EXPORT = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/REQUEST_POST_PERFORMANCE_EXPORT',
  RECEIVE_POST_PERFORMANCE_EXPORT_SUCCEEDED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_POST_PERFORMANCE_EXPORT_SUCCEEDED',
  RECEIVE_POST_PERFORMANCE_EXPORT_FAILED = 'INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS/RECEIVE_POST_PERFORMANCE_EXPORT_FAILED',
}

export interface IInstagramAccountEngagementPostsState
  extends IEngagementPageParameters {
  dateRange: IUnisonDateRange;
  flags: IInstagramAccountEngagementPostsFlags;
  posts: IInstagramPost[];
  postType: EngagementPostType;
  totalPostCount: number;

  postViewType: EngagementPostView;

  labels: ILabel[];
  selectedLabels: PojoSet<string>;

  selectedTrendGraphValue: EngagementPostsTrendGraphValue;
  aggregateStats?: IEngagementPostsAggregateStats;
  aggregateComparisonStats?: IEngagementPostsAggregateStats;
  trendStats?: IEngagementPostsTrendStats;

  // Used to power the recent posts table on the summary page
  recentPosts: IInstagramPost[];

  // Used to power the feed preview on the schedule page
  livePosts: IInstagramPost[];
  fetchLivePostsError?: string;
  fetchLabelsError?: string;

  mentionedTaggedPosts: IContentMedia[];

  fetchCommentsExportError?: string;
  fetchExportPostPerformanceToEmailError?: string;
  fetchPostPerformanceExportError?: string;
}

export enum EngagementPostView {
  Table = 'tableView',
  Grid = 'gridView',
}

export type IEngagementPostsTrendStats = IEngagementPostsTrendStat[];
export interface IEngagementPostsTrendStat {
  totalEngagementRate: number;
  totalFollowerEngagementRate: number;
  averageReach: number;
  postCount: number;
  date: Date;
  readableDate: string;
}

export enum EngagementPostsTrendGraphValue {
  TotalEngagementRate = 'totalEngagementRate',
  TotalFollowerEngagementRate = 'totalFollowerEngagementRate',
  AverageReach = 'averageReach',
  Posts = 'postCount',
}

export type IEngagementPostsAggregateStats = Record<
  EngagementPostsAggregateStatValue,
  number
>;
export enum EngagementPostsAggregateStatValue {
  AverageReach = 'AverageReach',
  TotalLikes = 'TotalLikes',
  TotalComments = 'TotalComments',
  TotalViews = 'TotalViews',
  EngagementRate = 'EngagementRate',
  FollowerEngagementRate = 'TotalFollowerEngagementRate',
}

export type InstagramEngagementSortKey = Extract<
  UniversalSearchSortKey,
  | UniversalSearchSortKey.Comments
  | UniversalSearchSortKey.EngagementRateWithReach
  | UniversalSearchSortKey.Likes
  | UniversalSearchSortKey.PostedTimestamp
  | UniversalSearchSortKey.Reach
  | UniversalSearchSortKey.Saves
  | UniversalSearchSortKey.Shares
  | UniversalSearchSortKey.Views
>;

export const InstagramEngagementSortKeys: Record<string, string> = {
  Comments: 'Comments',
  EngagementRateWithReach: 'EngagementRateWithReach',
  Likes: 'Likes',
  PostedTimestamp: 'PostedTimestamp',
  Reach: 'Reach',
  Saves: 'Saves',
  Shares: 'Shares',
  Views: 'Views',
};

export interface IEngagementPageParameters {
  limit: number;
  offset: number;
  sortKey: InstagramEngagementSortKey;
  sortDir: UniversalSearchSortDirection;
}

interface IInstagramAccountEngagementPostsFlags {
  isFetchingPosts: boolean;
  isFetchingPostsStats: boolean;
  isFetchingLivePosts: boolean;
  isFetchingRecentPosts: boolean;
  isFetchingMentionedTaggedPosts: boolean;
  isFetchingLabels: boolean;
  hasFetchedPosts: boolean;
  hasFetchedPostsStats: boolean;
  isFetchingCommentsExport: boolean;
  isFetchingPostPerformanceExport: boolean;
  isFetchingExportPostPerformanceToEmail: boolean;
}
